import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { HallsCapacity } from "views/infoDisplay/types";
import { useOrientation } from "./OrientationContext";
import { getFontSize, getRect, useOnResize } from "./sizeUtils";

const capacitySeparator = ": ";
const capacityFontSizeFactor = 1.5;

type CapacityItem = { label: string; capacity: number };

export const InfoDisplayCapacityView = ({
  capacity,
}: {
  capacity: HallsCapacity;
}) => {
  const capacityItems = useMemo(
    () =>
      Object.values(capacity).filter(
        (c): c is CapacityItem => c.capacity !== null
      ),
    [capacity]
  );

  if (!capacityItems.length) return null;
  return <VisibleView capacityItems={capacityItems} />;
};

const VisibleView = ({ capacityItems }: { capacityItems: CapacityItem[] }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { rotatedToSide } = useOrientation();
  const [space, setAvailableSpace] = useState({ width: 0, height: 0 });
  const fontSize = useMemo(() => {
    const rowHeight = space.height / capacityItems.length;
    const capacityLengthToFit = Math.max(
      ...capacityItems.map(
        (h) => h.capacity.toString().length * capacityFontSizeFactor
      )
    );
    const labelLengthToFit = Math.max(
      ...capacityItems.map((h) => h.label.length + capacitySeparator.length)
    );

    return getFontSize(
      labelLengthToFit + capacityLengthToFit,
      space.width,
      rowHeight,
      1 / 3
    );
  }, [capacityItems, space]);

  const handleResize = useCallback(() => {
    setAvailableSpace(getRect(ref.current, rotatedToSide));
  }, [rotatedToSide]);
  useOnResize(handleResize);
  useEffect(handleResize, [fontSize, handleResize]);

  return (
    <View>
      <Container>
        <Content ref={ref} style={{ fontSize: `${fontSize}px` }}>
          {capacityItems.map((h) => (
            <Row key={h.label}>
              <Label>
                {h.label}
                {capacitySeparator}
              </Label>
              <Capacity hasAvailable={h.capacity > 0}>{h.capacity}</Capacity>
            </Row>
          ))}
        </Content>
      </Container>
    </View>
  );
};

const View = styled.div`
  flex: 1 0 0%;
  max-height: 100%;
  overflow: hidden;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => `5${theme.vh} 5${theme.vw}`};
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  color: #fff;
  line-height: 1;
`;

const Row = styled.div`
  font-family: "Noto Mono", Consolas, monospace;
  display: flex;
  flex: 0 0 0%;
  height: 0;
  overflow: visible;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const Label = styled.div``;

const Capacity = styled.span<{ hasAvailable: boolean }>`
  color: ${(p) => (p.hasAvailable ? "#7eb550" : "var(--live)")};
  font-size: ${capacityFontSizeFactor}em;
`;
